import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { A, sx } from '../parts';

/** The footer note on every point-of-care sheet (design pwLayer). */
export const CONNECTION_NOTE = 'The console needs a connection. The phone app keeps charting offline and syncs later.';

/**
 * The design's point-of-care sheet (pwLayer, "Point of care"): a 600px panel over a scrim,
 * a title with Close, the content, and a footer with the connection note and one action.
 */
export function PocDialog({ title, onClose, children, action, testId }: { title: string; onClose: () => void; children: ReactNode; action?: ReactNode; testId?: string }) {
  const id = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <>
      <div aria-hidden="true" onMouseDown={onClose} style={sx('position:fixed;inset:0;z-index:90;background:rgba(17,20,24,.38);')} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-t`}
        data-testid={testId}
        style={sx('position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:91;width:600px;max-width:calc(100vw - 40px);max-height:86vh;box-sizing:border-box;display:flex;flex-direction:column;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);border-radius:12px;box-shadow:0 14px 40px rgba(34,39,46,.18);')}
      >
        <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:10px;padding:14px 18px;border-bottom:1px solid rgba(34,39,46,.1);')}>
          <span id={`${id}-t`} style={sx('flex:1 1 auto;font-size:19px;font-weight:800;letter-spacing:-.015em;color:#22272E;')}>
            {title}
          </span>
          <A style="font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;" onClick={onClose}>
            Close
          </A>
        </div>
        <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;padding:14px 18px;display:flex;flex-direction:column;gap:8px;')}>{children}</div>
        <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:12px 18px;border-top:1px solid rgba(34,39,46,.1);')}>
          <span style={sx('flex:1 1 220px;min-width:0;font-size:14px;color:#54606C;line-height:1.4;')}>{CONNECTION_NOTE}</span>
          {action}
        </div>
      </div>
    </>
  );
}

/** The design's point-of-care toast (pwToast). */
export function PocToast({ text }: { text: string }) {
  return (
    <div role="status" data-testid="poc-toast" style={sx('position:fixed;left:50%;bottom:28px;transform:translateX(-50%);z-index:92;max-width:calc(100vw - 40px);padding:11px 16px;border-radius:10px;background:#22272E;color:#FFFFFF;font-size:16px;font-weight:600;box-shadow:0 10px 30px rgba(34,39,46,.25);')}>
      {text}
    </div>
  );
}

export type PocMode = 'say' | 'chart' | 'incident' | 'inbox' | 'scan' | 'count' | 'trends';

/** The design's microphone on Say it once (pwLayer). */
const MIC = (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v3" />
  </svg>
);

/** The Care Staff tool row above the Worklist tabs (design pwLayer, pwCan), in the design's order. */
export function NurseToolsRow({ inboxCount, disabled, title, onOpen }: { inboxCount: number | null; disabled: boolean; title?: string; onOpen: (m: PocMode) => void }) {
  const plain = 'display:inline-flex;align-items:center;min-height:38px;padding:0 14px;border-radius:9px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-size:16px;font-weight:700;color:#22272E;text-decoration:none;white-space:nowrap;';
  const [more, setMore] = useState(false);
  return (
    <div data-testid="nurse-tools" style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:12px;')}>
      <A
        style="display:inline-flex;align-items:center;gap:8px;min-height:38px;padding:0 16px;border-radius:9px;background:#264C7E;font-size:16px;font-weight:700;color:#FFFFFF;text-decoration:none;white-space:nowrap;"
        onClick={() => onOpen('say')}
        disabled={disabled}
        title={title}
      >
        {MIC}
        Say it once
      </A>
      <A
        style="display:inline-flex;align-items:center;min-height:38px;padding:0 16px;border-radius:9px;border:1px solid rgba(47,92,151,.45);background:#FFFFFF;font-size:16px;font-weight:700;color:#264C7E;text-decoration:none;white-space:nowrap;"
        onClick={() => onOpen('chart')}
        disabled={disabled}
        title={title}
      >
        Chart
      </A>
      <A
        style="display:inline-flex;align-items:center;min-height:38px;padding:0 16px;border-radius:9px;border:1px solid rgba(179,38,30,.45);background:#FFFFFF;font-size:16px;font-weight:700;color:#B3261E;text-decoration:none;white-space:nowrap;"
        onClick={() => onOpen('incident')}
        disabled={disabled}
        title={title}
      >
        Report incident
      </A>
      {/* V7: the less used tools fold behind "More tools" (design pwMore). */}
      <button
        type="button"
        className="wl-a"
        aria-expanded={more}
        onClick={() => setMore((v) => !v)}
        style={sx('display:inline-flex;align-items:center;min-height:38px;padding:0 14px;border-radius:9px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-size:16px;font-weight:700;color:#22272E;text-decoration:none;white-space:nowrap;')}
      >
        {more ? 'Fewer tools ▴' : 'More tools ▾'}
      </button>
      {more && (
        <>
          <A style={plain} onClick={() => onOpen('inbox')} disabled={disabled} title={title}>
            {inboxCount === null ? 'Inbox' : `Inbox · ${inboxCount}`}
          </A>
          <A style={plain} onClick={() => onOpen('scan')} disabled={disabled} title={title}>
            Med pass scan
          </A>
          <A style={plain} onClick={() => onOpen('count')} disabled={disabled} title={title}>
            Narcotic count
          </A>
          <A style={plain} onClick={() => onOpen('trends')} disabled={disabled} title={title}>
            Resident trends
          </A>
        </>
      )}
    </div>
  );
}
